import { ItemsPerPage, useItemsPerPage } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'

import { QuoteListFilter } from '@features/invoices/enums/quoteListFilter'
import { QuotesSortProperty } from '@features/invoices/enums/quotesSortProperty'

import { PeriodMode } from '../../../../../enums/periodMode'
import { SortDirection } from '../../../../../enums/sortDirection'
import { useQueryParams } from '../../../../../hooks/useQueryParams'
import { useQuotesList } from './quotesList'

export interface QuotesListFilters {
  customerId?: string
  page?: number
  pageSize?: ItemsPerPage
  period?: string
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: QuotesSortProperty
  status: QuoteListFilter
}

export const defaultQuery: QuotesListFilters = {
  page: 1,
  pageSize: undefined,
  period: PeriodMode.All,
  searchQuery: undefined,
  sortDirection: SortDirection.Desc,
  sortProperty: QuotesSortProperty.Date,
  status: QuoteListFilter.All,
}

type ContextState = [QuotesListFilters, (filters: Partial<QuotesListFilters>) => void]

export interface QuotesListFiltersProviderProps {
  children?: ReactNode
  contextFilters?: Partial<QuotesListFilters>
}

const QuotesListFiltersContext = createContext<ContextState | undefined>(undefined)

export const QuotesListFiltersProvider = ({
  children,
  contextFilters,
}: QuotesListFiltersProviderProps): ReactElement => {
  const { options } = useQuotesList()
  const [pageSize] = useItemsPerPage()
  const [queryParams, setQueryParams] = useQueryParams<QuotesListFilters>({
    enabled: options.persistFiltersInUrl,
    defaultQuery: {
      ...defaultQuery,
      ...contextFilters,
      pageSize,
    },
  })

  const filters = useMemo(
    () => ({
      ...queryParams,
      // Sometimes `page` comes as a string. It needs to be always `number` type.
      page: Number(queryParams.page),
      // Sometimes `pageSize` comes as string. It needs to be always `number` type.
      pageSize: Number(queryParams.pageSize) as ItemsPerPage,
    }),
    [queryParams],
  )

  const setFilters = useCallback(
    (newFilters: Partial<QuotesListFilters>) => {
      setQueryParams(newFilters)
    },
    [setQueryParams],
  )

  const value = useMemo((): ContextState => [filters, setFilters], [filters, setFilters])

  return <QuotesListFiltersContext.Provider value={value}>{children}</QuotesListFiltersContext.Provider>
}

export const useQuotesListFilters = () => {
  const context = useContext(QuotesListFiltersContext)

  if (!context) {
    throw new Error('QuotesListFiltersContext is missing in the module!')
  }

  return context
}
